import React, { Children, isValidElement } from 'react';

import cn from 'classnames';

import IconButton, { iconColor } from '@components/Button/IconButton';
import { useOutsideClick } from '@hooks';

import OutlineButton from '../Button/OutlineButton';
import SolidButton from '../Button/SolidButton';
import Icon from '../Icon';

import ButtonGroupBase from './ButtonGroupBase';

const mapTypeToButton = {
  solid: SolidButton,
  outline: OutlineButton,
};

interface SplitButtonBaseProps {
  kind: 'solid' | 'outline';
  size?:
    | React.ComponentProps<typeof SolidButton>['size']
    | React.ComponentProps<typeof OutlineButton>['size'];
  primaryActionLabel: string;
  primaryActionIcon?: React.ReactElement;
  onClick?: () => void;
  children:
    | React.ReactNode
    | ((params: { closeDropdown: () => void }) => React.ReactNode);
  alignDropdownItems?: 'right' | 'left';
}

const SplitButtons = ({
  alignDropdownItems = 'right',
  size,
  kind,
  primaryActionIcon,
  primaryActionLabel,
  onClick,
  children,
}: SplitButtonBaseProps) => {
  const { open, setOpen, ref } = useOutsideClick();

  const Elem = mapTypeToButton[kind];

  return (
    <div className="relative block" ref={ref}>
      {onClick ? (
        <>
          <ButtonGroupBase groupType="split">
            <Elem
              leftIcon={primaryActionIcon}
              variant="primary"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onClick}
              size={size}
            >
              {primaryActionLabel}
            </Elem>
            <IconButton
              iconName="chevronDown"
              kind={kind}
              variant="primary"
              onClick={() => setOpen(!open)}
            />
          </ButtonGroupBase>
        </>
      ) : (
        <Elem
          variant="primary"
          size={size}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            setOpen(!open);
          }}
          rightIcon={
            <Icon name="chevronDown" color={iconColor[kind].primary} />
          }
        >
          {primaryActionLabel}
        </Elem>
      )}

      {open && (
        <div
          className={cn(
            'border-sp-neutral-100 absolute z-50 mt-2 w-56 rounded-lg border bg-white shadow-lg',
            {
              'right-0': alignDropdownItems === 'right',
              'left-0': alignDropdownItems === 'left',
            },
          )}
        >
          {typeof children === 'function'
            ? children({ closeDropdown: () => setOpen(false) })
            : children}
        </div>
      )}
    </div>
  );
};

export const SplitButtonItem = ({
  children,
  onClick,
  disabled,
  className,
}: {
  children: React.ReactNode;
  onClick: React.MouseEventHandler;
  disabled: boolean;
  className: string;
}) => {
  return (
    <a
      onClick={onClick}
      className={cn(
        `sp-dropdown-menu-item text-sp-neutral`,
        {
          'text-sp-neutral-200 pointer-events-none': disabled,
        },
        className,
      )}
    >
      {children}
    </a>
  );
};

SplitButtons.Item = SplitButtonItem;

SplitButtons.defaultProps = {
  kind: 'solid',
};

SplitButtonItem.defaultProps = {
  disabled: false,
  className: null,
};

export default SplitButtons;
